import _ from "lodash";
import {Clausepart, DocumentSection} from "common/types/document";
import {
  parseClauseObject,
  ProcessedClauseTemplate,
} from "./parse_clause_object";
import {usePreviousTemplateResults} from "./use_previous_template_results";

export interface RegexClauseData {
  regExpExec: RegExpExecArray;
  clauseData?: ProcessedClauseTemplate;
}
type ClauseDataProcessFn = (
  match: string,
  currentMatches: RegexClauseData[],
) => ProcessedClauseTemplate;

function processMatch(
  {regExpExec, clauseData}: RegexClauseData,
  currentMatches: RegexClauseData[],
  clauseDataProcessFn: ClauseDataProcessFn,
): RegexClauseData {
  if (clauseData && (!clauseData.hasFailed || clauseData.otherDocument)) {
    return {regExpExec, clauseData};
  }

  let newClauseData;
  try {
    const match = regExpExec[1].replace(/\\"/g, '"');
    newClauseData = clauseDataProcessFn(match, currentMatches);
  } catch (err) {
    newClauseData = {hasFailed: true, text: ""};
  }

  return {regExpExec, clauseData: newClauseData};
}

export const processClauseLinkTemplate = _.memoize(
  function processClauseLinkTemplateImpl(
    text: string,
    documentClauseparts: Clausepart[],
    documentSections: DocumentSection[],
    documentId?: number,
  ): RegexClauseData[] {
    let matches = Array.from(text.matchAll(/\<\<(.*?)\>\>/g)).map(
      regExpExec => ({
        regExpExec,
      }),
    );

    const fns: ClauseDataProcessFn[] = [
      (match: string) =>
        parseClauseObject(
          match,
          documentClauseparts,
          documentSections,
          documentId,
        ),
      (match: string, matches: RegexClauseData[]) =>
        usePreviousTemplateResults(match, matches),
    ];

    matches = fns.reduce(
      (acc, fn) => acc.map(match => processMatch(match, acc, fn)),
      matches,
    );

    return addSequentialNumbering(matches);
  },
  clauseTemplateCacheKey,
);

// Keyed on the data identity too: the store can hold another family
// document's clauses while the document itself has already switched
export function clauseTemplateCacheKey(
  text: string,
  documentClauseparts: Clausepart[],
  documentSections: DocumentSection[],
  documentId?: number,
): string {
  return [
    documentId ?? "",
    documentClauseparts.length,
    documentClauseparts[0]?.id ?? "",
    documentSections.length,
    documentSections[0]?.id ?? "",
    text,
  ].join(":");
}

function addSequentialNumbering(matches: RegexClauseData[]): RegexClauseData[] {
  const textCounts: Record<string, number> = {};
  for (const {clauseData} of matches) {
    if (
      clauseData &&
      !clauseData.hasFailed &&
      clauseData.isInUnnumberedSection
    ) {
      const text = clauseData.text;
      textCounts[text] = (textCounts[text] || 0) + 1;
    }
  }

  const textIndices: Record<string, number> = {};
  return matches.map(match => {
    const {clauseData} = match;
    if (
      !clauseData ||
      clauseData.hasFailed ||
      !clauseData.isInUnnumberedSection
    ) {
      return match;
    }
    const text = clauseData.text;
    if (textCounts[text] <= 1) {
      return match;
    }
    textIndices[text] = (textIndices[text] || 0) + 1;
    return {
      ...match,
      clauseData: {...clauseData, text: `${text} #${textIndices[text]}`},
    };
  });
}
